// Achievement badges: earned in-game, persisted locally, and sent as `awards`
// on the score claim — the claim service issues them as NIP-58 badge awards
// (kind 8, against kind 30009 definitions) signed by the game key.

export interface AwardDef {
  id: string;
  name: string;
  desc: string;
  emoji: string;
}

export const AWARDS: AwardDef[] = [
  { id: 'first-beam', name: 'FIRST CONTACT', desc: 'Abduct your first animal', emoji: '🛸' },
  { id: 'gravity', name: 'GRAVITY LESSON', desc: 'Splat an animal from altitude', emoji: '🩸' },
  { id: 'bowling', name: 'HERD BOWLING', desc: 'Flatten a normie with a dropped animal', emoji: '🎳' },
  { id: 'council', name: 'COUNCIL OF 600', desc: 'Assemble the council of twelve in one run', emoji: '👁' },
  { id: 'signal', name: 'THE SIGNAL', desc: 'Complete the tier-5 crop formation', emoji: '₿' },
  { id: 'combine', name: 'SCORCHED EARTH', desc: 'Stop three field combines in one run', emoji: '🌾' },
  { id: 'taxman', name: 'AUDIT THE AUDITOR', desc: 'Bring down THE AGENCY', emoji: '🚁' },
  { id: 'chain4', name: 'CHAIN REACTION', desc: 'Reach a x4 abduction chain', emoji: '⚡' },
  { id: 'chain8', name: 'UNBROKEN', desc: 'Reach a x8 abduction chain', emoji: '⛓' },
  { id: 'carwash', name: 'CAR WASH', desc: 'Hold the saucer under the lake surface', emoji: '🫧' },
  { id: 'fever', name: 'GOLDEN HOUR', desc: 'Trigger 600B MODE', emoji: '🌟' },
  { id: 'storm', name: 'STORM RIDER', desc: 'Clear a storm wave', emoji: '⛈' },
  { id: 'wave10', name: 'DOUBLE DIGITS', desc: 'Reach wave 10', emoji: '🌊' },
  { id: 'normie5', name: 'CROWD CONTROL', desc: 'Abduct five normies in one run', emoji: '👨‍🌾' },
  { id: 'stack', name: 'FULL STACK', desc: 'Install five perks in one run', emoji: '🥞' },
  { id: 'supply', name: 'AIRDROP', desc: 'Beam up a mothership supply drop', emoji: '📦' },
  { id: 'heifer', name: 'GOLDEN CALF', desc: 'Abduct the golden calf (it is entirely red)', emoji: '🐄' },
  { id: 'block21', name: '21 MILLION', desc: 'Reach wave 21 — the final block', emoji: '⛏' },
  { id: 'moonrock', name: 'MOON ROCKS', desc: 'Beam up meteor ore, still warm', emoji: '☄️' },
  { id: 'singularity', name: 'EVENT HORIZON', desc: 'Feed eight souls to one black hole', emoji: '🕳' },
  { id: 'rustler', name: 'CATTLE RUSTLER', desc: 'Steal a cow off a moving convoy truck', emoji: '🚚' },
  { id: 'donkeywork', name: 'DONKEY WORK', desc: 'Ferry the whole loose herd back into The Donkey Farm', emoji: '🫏' },
  { id: 'bloodmoon', name: 'BLOOD MOON HARVEST', desc: 'Clear a blood moon wave', emoji: '🌘' },
  { id: 'flock', name: 'THE FULL 600', desc: 'Deliver all 31 souls across your runs', emoji: '⛪' },
  { id: 'tour', name: 'SEE THE WORLD', desc: 'Complete a council and depart for a new land', emoji: '🗺' },
  { id: 'grandtour', name: 'THE GRAND TOUR', desc: 'Reach the third land of one run', emoji: '🌍' },
  { id: 'worldtour', name: 'THE WORLD TOUR', desc: 'Reach the fifth land of one run — every land on Earth in one night', emoji: '🛰' },
  { id: 'offworld', name: 'OFF WORLD', desc: 'Reach the sixth land of one run — leave the planet entirely', emoji: '🔴' },
  { id: 'nearside', name: 'THE NEAR SIDE', desc: 'Reach the seventh land of one run — the whole tour, ending on the Moon', emoji: '🌒' },
  { id: 'daily7', name: 'SEVEN DAY SIGNAL', desc: 'Complete the daily contract seven days in a row', emoji: '📡' },
];

const KEY = 'takemetoyourledger:awards:v1';

type AwardStore = Record<string, number>;

function load(): AwardStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as AwardStore;
  } catch { /* ignore */ }
  return {};
}

function save(store: AwardStore): void {
  try { localStorage.setItem(KEY, JSON.stringify(store)); } catch { /* ignore */ }
}

export function isUnlocked(id: string): boolean {
  return load()[id] !== undefined;
}

/** Returns true when this call newly unlocked the badge. */
export function unlock(id: string): boolean {
  const store = load();
  if (store[id] !== undefined) return false;
  store[id] = Date.now();
  save(store);
  return true;
}

export function awardState(): { def: AwardDef; unlockedAt: number | null }[] {
  const store = load();
  return AWARDS.map((def) => ({ def, unlockedAt: store[def.id] ?? null }));
}

export function awardDef(id: string): AwardDef | undefined {
  return AWARDS.find((a) => a.id === id);
}
